import React, { forwardRef, useLayoutEffect } from 'react';
import type { ForwardedRef } from 'react';

import type {
    EditableTextElement,
    EditableTextCommonEvent,
    EditableTextKeyDownEvent,
    EditableTextPasteEvent,
    EditableTextValue,
    EditableTextValueHandler,
    EditableTextBlurHandler,
    EditableTextKeyDownHandler,
} from './types';
import {
    keyEventCode,
    allowedSingleKeys,
    allowedComboKeys,
    commonRegExp,
    escFirefoxBlurFix,
    getCharacterCount,
    getTextFromEventTarget,
    insertTextAtPosition,
    trimClipboardToFitLimit,
    sanitizeInput,
    replaceEditableValue,
    setSelectionPosition,
    windowHasTextSelection,
} from './utilities';

import './EditableText.scss';

export interface EditableTextProps {
    id: string;
    value?: EditableTextValue;
    placeholder?: string;
    // TODO: Should be renamed `maxLength`.
    characterLimit?: number;
    filter?: RegExp;
    editing?: boolean;
    onFocus?: EditableTextValueHandler;
    onBlur?: EditableTextBlurHandler;
    onClick?: EditableTextValueHandler;
    onChange?: EditableTextValueHandler;
    onKeyDown?: EditableTextKeyDownHandler;
    onUpdateValue: EditableTextValueHandler;
}

function EditableTextComponent(
    {
        id,
        value,
        placeholder,
        characterLimit,
        filter = commonRegExp.lineBreaks,
        editing = false,
        onFocus,
        onBlur,
        onClick,
        onChange,
        onKeyDown,
        onUpdateValue,
    }: EditableTextProps,
    ref: ForwardedRef<EditableTextElement>,
) {
    const sanitizedValue = value ? sanitizeInput(value, filter) : value;
    const controlledContent = sanitizedValue ? sanitizedValue : placeholder;

    function handleFocus(event: EditableTextCommonEvent) {
        const currentInputText = getTextFromEventTarget(event);

        onUpdateValue(currentInputText);
        onFocus?.(currentInputText, getCharacterCount(currentInputText, characterLimit));
    }

    function handleBlur(event: EditableTextCommonEvent, bypassUpdate = false) {
        const currentInputText = bypassUpdate ? sanitizedValue : getTextFromEventTarget(event);

        const sanitizedInputText = currentInputText ? sanitizeInput(currentInputText, filter) : currentInputText;

        // We call this every time (not just when `bypassUpdate`)
        // because `contenteditable` is an uncontrolled element.
        // The `value` prop coming in might be the same
        // as the previous render, but the result of sanitizeInput()
        // may not match the typed value within `contenteditable`.
        replaceEditableValue(event.currentTarget, sanitizedInputText);

        if (!bypassUpdate) {
            onUpdateValue(sanitizedInputText);
        }

        onBlur?.(sanitizedInputText, bypassUpdate);
    }

    function handleClick(event: EditableTextCommonEvent) {
        const currentInputText = getTextFromEventTarget(event);
        onClick?.(currentInputText, getCharacterCount(currentInputText, characterLimit));
    }

    function handleInput(event: EditableTextCommonEvent) {
        const currentInputText = getTextFromEventTarget(event);
        onChange?.(currentInputText, getCharacterCount(currentInputText, characterLimit));
    }

    function handleKeyDown(event: EditableTextKeyDownEvent) {
        // TODO: Currenly `onKeyDown` doesn't allow a consumer to
        // break out of the rest of the `handleKeyDown` execution.
        // We could allow this if we find it to be useful.
        onKeyDown?.(event.key);

        if (event.key === keyEventCode.diverted.enter) {
            event.preventDefault();
            // TODO: Manually calling `.blur()` is required
            // to resolve a FF bug where `:focus` remains
            // on the `<span />` and a subsequent `onFocus`
            // causes `onBlur` to run right before `:focus`.
            // If ever resolved, we can return to calling:
            // handleBlur(event);
            event.currentTarget.blur();
            return;
        }

        if (event.key === keyEventCode.diverted.escape) {
            // TODO: Same as before, but more complicated
            // as we want to pass `bypass=true` to `handleBlur`.
            // handleBlur(event, true);
            escFirefoxBlurFix(event.currentTarget, sanitizedValue);
            return;
        }

        const currentInputText = getTextFromEventTarget(event) ?? '';

        const allowSingleKey = allowedSingleKeys.includes(event.key);
        const allowComboKey = (event.ctrlKey || event.metaKey) && allowedComboKeys.includes(event.key);

        const allowKeyEvent = allowSingleKey || allowComboKey;

        if (
            characterLimit &&
            currentInputText.length >= characterLimit &&
            !windowHasTextSelection() &&
            !allowKeyEvent
        ) {
            event.preventDefault();
        }
    }

    function handlePaste(event: EditableTextPasteEvent) {
        event.preventDefault();

        const selection = window.getSelection();

        if (!selection) {
            return;
        }

        const selectionText = selection.toString();
        const selectionLength = selectionText.length;

        const selectionStart = Math.min(selection.anchorOffset, selection.focusOffset);
        const selectionEnd = selectionStart + selectionLength;

        const singlePoint = selectionStart === selectionEnd;

        const currentInputText = getTextFromEventTarget(event) ?? '';
        // You could argue that atleast `1` leading/trailing space
        // should be retained depending on the paste position
        // (beginning vs end). If so, I have a `softTrim()` function to use.
        const clipboardText = sanitizeInput(event.clipboardData.getData('text/plain'), filter);

        const trimmedClipboard = characterLimit
            ? trimClipboardToFitLimit({
                  currentInputText,
                  clipboardText,
                  characterLimit,
                  selectionLength,
              })
            : clipboardText;

        const mergedText = insertTextAtPosition({
            primaryText: currentInputText,
            secondaryText: trimmedClipboard,
            position: singlePoint
                ? selectionStart
                : {
                      start: selectionStart,
                      end: selectionEnd,
                  },
        });

        const newPosition = selectionStart + trimmedClipboard.length;

        replaceEditableValue(event.currentTarget, mergedText);
        setSelectionPosition(event.currentTarget, newPosition, selection);

        onChange?.(mergedText, getCharacterCount(mergedText, characterLimit));
    }

    useLayoutEffect(() => {
        // TODO: The <span ref /> gets reported as a `LegacyRef`,
        // but this shouldn't ever actually be a function.
        if (editing && typeof ref !== 'function') {
            ref?.current?.focus();
        }
    }, [editing, ref]);

    // TODO: Since `value` and `placeholder` can both be undefined,
    // should we set some min-width/height styles?
    return (
        <span
            id={id}
            ref={ref}
            className="EditableText"
            spellCheck={false}
            contentEditable={editing}
            suppressContentEditableWarning={editing}
            onFocus={handleFocus}
            onBlur={handleBlur}
            onClick={handleClick}
            onInput={handleInput}
            onKeyDown={handleKeyDown}
            onPaste={handlePaste}
        >
            {controlledContent}
        </span>
    );
}

export const EditableText = forwardRef(EditableTextComponent);
